package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.state.AirportFavorite
import com.banyan.flightviewer.state.Bookmark
import com.banyan.flightviewer.web.components.util.formatBookmarkTimestamp
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.dom.span
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.coroutines.launch
import org.w3c.dom.Element

/**
 * Toggleable favorites panel. Visibility is controlled by
 * [com.banyan.flightviewer.web.state.WebUiState.bookmarksVisible] — the ★
 * icon in the TopBar flips it (and auto-switches the mobile view to the
 * List tab so this panel is actually reachable there).
 *
 * Contains TWO sections:
 *  - **Flights** — bookmarked aircraft. Tap a row to select that flight.
 *  - **Airports** — favorited airports. Tap a row to jump the map there
 *    and open the AirportPanel.
 *
 * Both sections render only when non-empty; if both are empty we show
 * a single empty-state message.
 */
fun mountBookmarksPanel(parent: Element, app: WebAppContainer) {
    val container = parent.div(className = "fv-bookmarks")
    container.asDynamic().style.display = "none"

    fun render() {
        container.removeAllChildren()
        val visible = app.uiState.bookmarksVisible.value
        if (!visible) {
            container.asDynamic().style.display = "none"
            return
        }
        container.asDynamic().style.display = "block"

        // Header: star + "Favorites" + spacer + close ×
        val header = container.div(className = "fv-bookmarks__header")
        header.span(className = "fv-bookmarks__star") { textContent = "\u2605" }
        header.span(className = "fv-bookmarks__title") { textContent = "Favorites" }
        header.div(className = "fv-bookmarks__spacer") { }
        header.button(className = "fv-bookmarks__close") {
            textContent = "\u2715"
            title = "Close"
            onClick { app.uiState.setBookmarksVisible(false) }
        }

        val bookmarks = app.appState.bookmarks.value
        val airports = app.appState.airportFavorites.value

        if (bookmarks.isEmpty() && airports.isEmpty()) {
            container.div(className = "fv-bookmarks__empty") {
                textContent = "No favorites yet. Tap the star on a flight " +
                    "or an airport to save it here."
            }
            return
        }

        // ── Flights section ───────────────────────────────────────────
        if (bookmarks.isNotEmpty()) {
            container.div(className = "fv-bookmarks__section-label") {
                textContent = "FLIGHTS"
            }
            container.div(className = "fv-bookmarks__list") {
                bookmarks.sortedByDescending { it.timestamp }.forEach { bm ->
                    renderFlightRow(this, app, bm)
                }
            }
        }

        // ── Airports section ──────────────────────────────────────────
        if (airports.isNotEmpty()) {
            container.div(className = "fv-bookmarks__section-label") {
                textContent = "AIRPORTS"
            }
            container.div(className = "fv-bookmarks__list") {
                airports.sortedByDescending { it.timestamp }.forEach { fav ->
                    renderAirportRow(this, app, fav)
                }
            }
        }
    }

    app.appState.scope.bindFlow(app.uiState.bookmarksVisible) { render() }
    app.appState.scope.bindFlow(app.appState.bookmarks) { render() }
    app.appState.scope.bindFlow(app.appState.airportFavorites) { render() }
}

// ── Flight row (existing behaviour, preserved) ─────────────────────────

private fun renderFlightRow(parent: Element, app: WebAppContainer, bm: Bookmark) {
    parent.div(className = "fv-bookmarks__item") {
        // Row 1: callsign + type badge + registration badge
        val top = div(className = "fv-bookmarks__row1")
        top.span(className = "fv-bookmarks__callsign") { textContent = bm.callsign }
        if (bm.aircraftType.isNotBlank() && bm.aircraftType != "Unknown") {
            top.span(className = "fv-bookmarks__badge") { textContent = bm.aircraftType }
        }
        if (bm.registration.isNotBlank()) {
            top.span(className = "fv-bookmarks__badge") { textContent = bm.registration.uppercase() }
        }
        top.div(className = "fv-bookmarks__spacer") { }
        top.span(className = "fv-bookmarks__time") {
            textContent = formatBookmarkTimestamp(bm.timestamp)
        }
        // Delete button is a button so keyboard users can hit it; stopPropagation
        // so clicking × doesn't also select the flight.
        val del = top.button(className = "fv-bookmarks__delete") {
            textContent = "\u2715"
            title = "Delete bookmark"
        }
        del.addEventListener("click", { ev: org.w3c.dom.events.Event ->
            ev.stopPropagation()
            app.appState.removeBookmark(bm.hex)
            app.settingsManager.saveBookmarks(app.appState.bookmarks.value)
        })

        // Row 2: airline + route
        val routeText = when {
            bm.origin.isNotBlank() && bm.destination.isNotBlank() ->
                "${bm.origin} \u2192 ${bm.destination}"
            bm.origin.isNotBlank() -> bm.origin
            bm.destination.isNotBlank() -> bm.destination
            else -> ""
        }
        val airline = bm.airline.takeIf { it.isNotBlank() } ?: ""
        val summary = when {
            airline.isNotBlank() && routeText.isNotBlank() -> "$airline \u00B7 $routeText"
            airline.isNotBlank() -> airline
            routeText.isNotBlank() -> routeText
            else -> bm.hex
        }
        div(className = "fv-bookmarks__row2") { textContent = summary }

        // Whole row selects the flight, hides this panel, and pans the
        // map to the aircraft. Live position first (no extra network
        // call if the plane is currently in view); fall back to an
        // ADSBX hex lookup when it's outside the current query area.
        onClick {
            app.appState.selectFlight(bm.hex)
            app.uiState.setBookmarksVisible(false)
            app.appState.scope.launch {
                val live = app.appState.aircraftList.value
                    .firstOrNull { it.hex.equals(bm.hex, ignoreCase = true) }
                val pos = live ?: app.serverClient.getAircraftByHex(bm.hex)
                if (pos != null) app.uiState.requestMapFocus(pos.lat, pos.lon)
            }
        }
    }
}

// ── Airport row ────────────────────────────────────────────────────────

private fun renderAirportRow(parent: Element, app: WebAppContainer, fav: AirportFavorite) {
    // Prefer live-cached name over the snapshot saved at favorite-time —
    // keeps the list self-healing if AirportDatabase picks up a better
    // name for the same code in a later build.
    val name = AirportDatabase.getName(fav.code)
        .takeIf { it != fav.code }
        ?: fav.name.ifBlank { fav.code }

    parent.div(className = "fv-bookmarks__item") {
        val top = div(className = "fv-bookmarks__row1")
        top.span(className = "fv-bookmarks__callsign") { textContent = fav.code.uppercase() }
        top.div(className = "fv-bookmarks__spacer") { }
        top.span(className = "fv-bookmarks__time") {
            textContent = formatBookmarkTimestamp(fav.timestamp)
        }
        val del = top.button(className = "fv-bookmarks__delete") {
            textContent = "\u2715"
            title = "Remove favorite"
        }
        del.addEventListener("click", { ev: org.w3c.dom.events.Event ->
            ev.stopPropagation()
            app.appState.removeAirportFavorite(fav.code)
            app.settingsManager.saveAirportFavorites(app.appState.airportFavorites.value)
        })

        div(className = "fv-bookmarks__row2") { textContent = name }

        // Whole row selects the airport and hides the panel. We also
        // fire an explicit map-focus request: the selectedAirport flow
        // bind already pans the map on value-change, but a same-value
        // set (clicking the already-selected airport's favorite row)
        // is a no-op for MutableStateFlow -- so the explicit focus
        // guarantees a recenter on every click.
        onClick {
            app.appState.selectAirport(fav.code)
            app.uiState.setBookmarksVisible(false)
            val apt = AirportDatabase.getAirportsWithLocations()
                .firstOrNull { it.code.equals(fav.code, ignoreCase = true) }
            if (apt != null) app.uiState.requestMapFocus(apt.lat, apt.lon)
        }
    }
}
